import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { Question } from '_models/question';
import { UserService } from '_services/user.service';
import {
  COMPOUND_INDEPENDENT_ID,
  DEPENDENCY_INPUT_IDS,
  STUDENT_FINANCES_PROFILE_IDS,
} from '_services/dependency-gateway';
import { AppCheckErrorService } from './app-check-error.service';

export interface PreparePlaygroundQuestionsOptions {
  isAidQuiz: boolean;
}

/** Assign optional dependency profile questions to Student Finances when a school provides them. */
export function assignStudentFinancesProfileSections(questions: Question[]): void {
  for (const id of STUDENT_FINANCES_PROFILE_IDS) {
    const question = questions.find(q => q.id === id);
    if (question) {
      question.section_id = 'SF';
    }
  }
}

/** Identity / meta fields collected outside form sections — leave as ST (hidden). */
const UNMAPPED_START_IDS = new Set([
  'student.firstName',
  'student.completingCalculator',
]);

/**
 * Neither product has a Start (ST) or DS form page. Move leftover questions into
 * Student Finances so they remain fillable. For NPC, leave dependency-gateway
 * inputs alone (checklist on Getting Started; birth year / marital already
 * assigned to SF above). Aid Quiz has no gateway page, so include those too.
 * Do not remap first name / who's completing.
 */
export function reassignOrphanQuestions(
  questions: Question[],
  options: { includeGatewayInputs?: boolean } = {},
): void {
  for (const question of questions) {
    if (UNMAPPED_START_IDS.has(question.id)) {
      continue;
    }
    if (!options.includeGatewayInputs && DEPENDENCY_INPUT_IDS.includes(question.id)) {
      continue;
    }
    if (question.section_id === 'ST' || question.section_id === 'DS') {
      question.section_id = 'SF';
    }
  }
}

function cloneQuestion(template: Question): Question {
  return JSON.parse(JSON.stringify(template));
}

/** Firestore questions do not include numbered sibling fields; expand templates here. */
function addSiblingAgeQuestions(questions: Question[]): void {
  for (let i = 1; i <= 6; i++) {
    const siblingAgeTemplate = questions.find(q => q.id === 'parent.siblingAge');
    if (siblingAgeTemplate) {
      const siblingAgeQ = cloneQuestion(siblingAgeTemplate);
      siblingAgeQ.id = 'parent.siblingAge'.concat(String(i));
      siblingAgeQ.format = 'Number';
      siblingAgeQ.ordinal++;
      questions.push(siblingAgeQ);
    }

    const siblingAttendCollegeTemplate = questions.find(q => q.id === 'parent.siblingAttendCollegeCode');
    if (siblingAttendCollegeTemplate) {
      const siblingAttendCollegeQ = cloneQuestion(siblingAttendCollegeTemplate);
      siblingAttendCollegeQ.id = 'parent.siblingAttendCollegeCode'.concat(String(i));
      siblingAttendCollegeQ.ordinal++;
      questions.push(siblingAttendCollegeQ);
    }

    const siblingCollegeTypeTemplate = questions.find(q => q.id === 'parent.siblingCollegeTypeCode');
    if (siblingCollegeTypeTemplate) {
      const siblingCollegeTypeQ = cloneQuestion(siblingCollegeTypeTemplate);
      siblingCollegeTypeQ.id = 'parent.siblingCollegeTypeCode'.concat(String(i));
      siblingCollegeTypeQ.ordinal++;
      questions.push(siblingCollegeTypeQ);
    }
  }
}

function addCompoundIndependentQuestion(questions: Question[]): void {
  const existing = questions.find(q => q.id === COMPOUND_INDEPENDENT_ID);
  if (existing) {
    existing.hidden = true;
    return;
  }

  questions.push({
    id: COMPOUND_INDEPENDENT_ID,
    value: '',
    format: 'Code',
    label: 'Independent student gateway',
    required: true,
    dependent: true,
    independent: true,
    indent: false,
    ordinal: 1090,
    help: '',
    section_id: 'SF',
    choices: [
      { label: 'Yes', value: 'yes', ord: 1 },
      { label: 'No', value: 'no', ord: 2 },
    ],
    triggerHide: false,
    hidden: true,
    efcMethodologyHidden: false,
  });
}

/** Normalizes Firestore questions into the playground question set. */
export function preparePlaygroundQuestions(
  questions: Question[],
  options: PreparePlaygroundQuestionsOptions,
): void {
  assignStudentFinancesProfileSections(questions);
  addSiblingAgeQuestions(questions);
  if (!options.isAidQuiz) {
    addCompoundIndependentQuestion(questions);
  }
  reassignOrphanQuestions(questions, { includeGatewayInputs: options.isAidQuiz });
}

/**
 * Loads the question set from Firestore for a school (NPC/Aid Quiz form definitions).
 * Populates QuestionService via the shared BehaviorSubject; state lives in QuestionService.
 */
@Injectable({
  providedIn: 'root'
})
export class QuestionsService {
  private afs = inject(Firestore);
  private userService = inject(UserService);
  private appCheckErrorService = inject(AppCheckErrorService);

  loadedSchoolId = '';
  questions = new BehaviorSubject<Question[]>([]);

  async loadQuestions(schoolId: string, draft: boolean): Promise<any> {
    if (schoolId !== this.loadedSchoolId) {
      const path = draft ? 'questions_draft/' : 'questions/';
      const questionsDoc = doc(this.afs, path + schoolId);
      return getDoc(questionsDoc).then(docSnapshot => {
          const data = docSnapshot.data() as { questions: Question[] } | undefined;
          const questions = data?.questions;
          if (questions) {
            const sortedQuestions = questions.sort((n1, n2) => n1.ordinal - n2.ordinal);
            this.setDefaults(sortedQuestions);
            preparePlaygroundQuestions(sortedQuestions, { isAidQuiz: this.userService.isAidQuiz });
            this.questions.next(sortedQuestions);
            this.loadedSchoolId = schoolId;
            return true;

          } else {
            const err = new Error('SchoolId: ' + schoolId + ' is missing questions in the cloud database.');
            err.name = 'NoQuestions';
            throw err;
          }
        }).catch(error => {
          this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
            operation: 'loadQuestions',
            schoolId,
            assumeAppCheck: true
          });
          throw error;
        });
    }
  }

  setDefaults(questions: Question[]): void {
    questions.forEach(q => {
      q.efcMethodologyHidden = false;
      q.triggerHide = false;
    });
  }
}
